import { differenceInHours, differenceInMinutes, differenceInSeconds, format, isYesterday, parseISO } from 'date-fns'
import { format as formatTz } from 'date-fns-tz'
import { type UtilsTranslationFunction } from '../types'

export { format as dateFormat } from 'date-fns'

export const dateFormatExtended = (date: string): string => {
  if (!date) return ''
  return format(new Date(date), 'EEE, MMM dd, yyyy')
}

// Date Formatter when a date is passed w/o a timezone
// W/o a Timezone returns GMT time -4 hours behind changing the date to previous day
export const formatDateNoTZ = (date: string, formatAppearance: string): string => {
  if (!date) return ''
  return format(parseISO(date), formatAppearance)
}

// util converts a card expiration date MM/yy string to a string in the format expected by BE "yyyy-MM-dd"
export function fromCardExpirationDateToBEDate(cardExpDate: string | undefined): string | undefined {
  if (!cardExpDate?.match(/\d\d\/\d\d/gm)) return

  const dateArray = cardExpDate.split('/')
  if (Number(dateArray[0]) > 12) return
  // changing date to mid-month to be safe against timezones, be doesn't care about the day
  const safeExpiryDate = new Date(`20${dateArray[1]}-${dateArray[0]}-15`)

  return format(safeExpiryDate, 'yyyy-MM-dd')
}

/**
 * Formats a date as a relative time string with enhanced formatting and internationalization support
 * @param date - Date object or timestamp to format
 * @param baseDate - Date to compare against (defaults to current time)
 * @returns Formatted time string with scenarios:
 *   - "Just now" (less than 1 minute)
 *   - "1m ago", "15m ago" (1-59 minutes)
 *   - "1h ago", "1h 32m ago", "2h ago" (1-23 hours, with minutes if greater than 0)
 *   - "Yesterday 3:45 PM ET" (yesterday)
 *   - "Sep 10, 2025 4:30 PM ET" (older dates)
 */
export const formatRelativeTimeWithTranslation = ({
  t,
  date,
  baseDate = new Date(),
}: {
  t: UtilsTranslationFunction
  date: Date | number
  baseDate?: Date
}): string => {
  const targetDate = typeof date === 'number' ? new Date(date) : date
  const easternTZ = 'America/New_York'

  const seconds = differenceInSeconds(baseDate, targetDate)
  const minutes = differenceInMinutes(baseDate, targetDate)
  const hours = differenceInHours(baseDate, targetDate)

  // Just now (< 1 minute)
  if (seconds < 60) {
    return t('date.relative.just_now')
  }

  // Minutes ago (1-59 minutes)
  if (minutes < 60) {
    return t('date.relative.minutes_ago', { count: minutes })
  }

  // Hours with optional minutes (1-23 hours)
  if (hours < 24) {
    const remainingMinutes = minutes - hours * 60

    if (remainingMinutes === 0) {
      return t('date.relative.hours_ago', { count: hours })
    }

    return t('date.relative.hours_with_minutes_ago', {
      hours,
      minutes: remainingMinutes,
    })
  }

  // Yesterday with time
  if (isYesterday(targetDate)) {
    const timeFormatted = formatTz(targetDate, 'h:mm a', { timeZone: easternTZ })

    return t('date.relative.yesterday_with_time', { time: timeFormatted })
  }

  // Older dates with full date and time
  const dateFormatted = formatTz(targetDate, 'MMM d, yyyy h:mm a', { timeZone: easternTZ })

  return t('date.relative.full_date_with_time', { date: dateFormatted })
}

/**
 * Get the last refreshed text
 * @param date - number to format
 * @returns Last refreshed text
 */
export const getLastRefreshedText = ({ t, timestamp }: { t: UtilsTranslationFunction; timestamp: number }): string => {
  const dateFormatted = formatRelativeTimeWithTranslation({ t, date: new Date(timestamp) })
  return t(`date.last_refreshed`, { date: dateFormatted })
}
